import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  zonesAPI,
  statesAPI,
  type Zone,
  type State,
  type StateCreatePayload,
} from "../../../services/api";

function getZoneName(state: State, zones: Zone[]) {
  if (state.zone_name) {
    return state.zone_name;
  }
  const id = state.zone;
  if (id === null) {
    return "—";
  }
  const z = zones.find(p => String(p.id) === String(id));
  return z?.name ?? String(id);
}

export default function StatePage() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    zone: string;
    is_active: boolean;
  }>({
    name: "",
    zone: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<State | null>(null);
  const [deleting, setDeleting] = useState<State | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchZones = async () => {
    try {
      const list = await zonesAPI.list();
      setZones(Array.isArray(list) ? list : []);
    } catch {
      setZones([]);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const list = await statesAPI.list();
      setStates(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load states");
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);
  useEffect(() => {
    fetchList();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("State name is required");
      return;
    }
    if (!form.zone) {
      toast.error("Please select a zone");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload: StateCreatePayload = {
        name: form.name.trim(),
        zone: Number(form.zone) || form.zone,
        is_active: form.is_active,
      };
      if (editing) {
        await statesAPI.update(editing.id, payload);
        toast.success("State updated successfully");
      } else {
        await statesAPI.create(payload);
        toast.success("State created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", zone: "", is_active: true });
      setEditing(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save state";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await statesAPI.delete(deleting.id);
      toast.success("State deleted successfully");
      setDeleting(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete state";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">State</h1>
        <p className="text-muted-foreground">Manage states</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>States ({states.length})</CardTitle>
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", zone: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    zone: zones[0]?.id !== null ? String(zones[0].id) : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit State" : "Create New State"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Zone *</Label>
                  <Select
                    value={form.zone}
                    onValueChange={v => setForm(prev => ({ ...prev, zone: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map(z => (
                        <SelectItem key={z.id} value={String(z.id)}>
                          {z.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="state-name">Name *</Label>
                  <Input
                    id="state-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="State name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="state-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="state-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : states.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No states yet. Create one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {states.map((s, i) => (
                  <TableRow key={s.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{getZoneName(s, zones)}</TableCell>
                    <TableCell>
                      {s.is_active !== false ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(s);
                            setForm({
                              name: s.name,
                              zone:
                                s.zone !== null
                                  ? String(s.zone)
                                  : zones[0]?.id !== null
                                    ? String(zones[0].id)
                                    : "",
                              is_active: s.is_active ?? true,
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(s)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete State</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
